import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { ComponentType, Component } from '@/types';
import { getActionCardConfig } from './actionCardRegistry';
import type { PropertyMessage } from '../types';
import { isMapChartComponent } from '@/types/component-properties';

interface ActionCardRef {
  componentId: string;
  messageId: string;
  marker: string;
  pinId?: string;
}

interface UseActionCardInjectionOptions {
  component: Component | null | undefined;
  setMessages: Dispatch<SetStateAction<PropertyMessage[]>>;
  generateMessageId: () => string;
  persistMessage: (msg: PropertyMessage) => void;
}

/**
 * Single hook replacing both pin and gauge useEffects for action card message injection.
 * Injects an action card message when a component with a registered card config is selected,
 * and removes it when the component changes or deselects.
 */
export function useActionCardInjection({
  component,
  setMessages,
  generateMessageId,
  persistMessage,
}: UseActionCardInjectionOptions) {
  const actionCardRef = useRef<ActionCardRef | null>(null);

  useEffect(() => {
    const mapComponent = component && isMapChartComponent(component) ? component : null;
    const componentType = component?.type;
    const componentId = component?.id;
    const props = component?.properties;

    // Determine if we need a pin-specific card (MAP_CHART with selectedPinId)
    const isMap = componentType === ComponentType.MAP_CHART;
    const selectedPinId = mapComponent?.properties.selectedPinId;

    // Get card config
    const config = componentType ? getActionCardConfig(componentType) : undefined;

    // Determine the effective marker and whether we should show a card
    let marker: string | undefined;
    let shouldShow = false;

    if (config) {
      if (config.category === 'pin') {
        // Pin cards only show when a pin is actually selected
        if (selectedPinId) {
          marker = config.marker;
          shouldShow = true;
        }
      } else {
        marker = config.marker;
        shouldShow = true;
        // For MAP_CHART: switch to pin-specific marker when a pin is selected
        if (isMap && selectedPinId) {
          marker = '◈ Pin selected:';
        }
      }
    }

    // Remove previous message if selection changed
    if (actionCardRef.current) {
      const prev = actionCardRef.current;
      const shouldRemove =
        !component ||
        prev.componentId !== componentId ||
        prev.marker !== marker ||
        (prev.pinId && prev.pinId !== selectedPinId);

      if (shouldRemove) {
        const messageIdToRemove = prev.messageId;
        actionCardRef.current = null;
        queueMicrotask(() => {
          setMessages(prev => prev.filter(m => m.id !== messageIdToRemove));
        });
      }
    }

    if (!shouldShow || !component || !componentId || !marker) return;

    // Already tracking a message for this exact component (and pin)
    if (actionCardRef.current?.componentId === componentId &&
        actionCardRef.current?.marker === marker &&
        (!selectedPinId || actionCardRef.current?.pinId === selectedPinId)) {
      return;
    }

    // Build message content
    let content: string;
    if (config!.category === 'pin' && selectedPinId) {
      const pins = mapComponent?.properties.pins ?? [];
      const pin = pins.find(p => p.id === selectedPinId);
      if (!pin) return;
      content = `${marker} "${pin.name}"\nModify this pin:`;
    } else if (isMap && selectedPinId) {
      // MAP_CHART with pin selected (simple category)
      const pins = mapComponent?.properties.pins ?? [];
      const pin = pins.find(p => p.id === selectedPinId);
      if (!pin) return;
      content = `${marker} "${pin.name}"\nModify this pin:`;
    } else if (config!.category === 'gauge') {
      const title = (props?.title as string) || (props?.name as string) || 'Gauge';
      content = `${marker} "${title}"`;
    } else {
      const title = config!.titleFn(props || {});
      content = `${marker} "${title}"`;
    }

    queueMicrotask(() => {
      let messageToPerist: PropertyMessage | null = null;

      setMessages(prev => {
        // Remove any stale action card with the same marker before injecting fresh.
        // This handles store-restored messages containing an old card the ref doesn't track.
        const filtered = prev.filter(m => !m.content.includes(marker!));

        const msg: PropertyMessage = {
          id: generateMessageId(),
          content,
          role: 'assistant',
          timestamp: new Date(),
        };
        actionCardRef.current = {
          componentId: componentId!,
          messageId: msg.id,
          marker: marker!,
          pinId: selectedPinId,
        };
        messageToPerist = msg;
        return [...filtered, msg];
      });

      if (messageToPerist) {
        queueMicrotask(() => persistMessage(messageToPerist!));
      }
    });
  }, [component?.id, component?.type, component?.properties?.selectedPinId]);

  return actionCardRef;
}
